import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useOptionalAuth } from '../../stores/auth';
import { ThemeToggle } from '../ui/ThemeToggle';
import { Icon } from '../ui/Icon';
import { SiteSwitcher } from './SiteSwitcher';
import '../../pages/marketing/marketing.css';

export function PublicHeader() {
  const user = useOptionalAuth()?.user ?? null;
  const [menuOpen, setMenuOpen] = useState(false);

  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="pl-header">
      <div className="sarnia-container pl-header__inner">
        <Link className="pl-logo" to="/" onClick={closeMenu}>
          <span className="pl-logo__mark" aria-hidden="true">
            <Icon name="zap" size={16} />
          </span>
          <span className="pl-logo__wordmark">Sarnia Builder</span>
        </Link>

        <nav className="pl-nav" aria-label="Main navigation">
          <Link className="pl-nav__link" to="/templates" onClick={closeMenu}>Templates</Link>
          <Link className="pl-nav__link" to="/pricing" onClick={closeMenu}>Pricing</Link>
          <Link className="pl-nav__link" to="/help/getting-started" onClick={closeMenu}>Guide</Link>
          {user ? (
            <Link className="pl-nav__link" to="/dashboard/sites" onClick={closeMenu}>Dashboard</Link>
          ) : (
            <Link className="pl-nav__link" to="/login" onClick={closeMenu}>Sign in</Link>
          )}
        </nav>

        <div className="pl-header__actions">
          <Link
            className="sarnia-btn sarnia-btn--primary sarnia-btn--sm"
            to={user ? '/dashboard/sites' : '/signup'}
            onClick={closeMenu}
          >
            {user ? 'Dashboard' : 'Start building'}
          </Link>
          <ThemeToggle />
          <button
            className="pl-menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="public-mobile-nav"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <Icon name={menuOpen ? 'x' : 'menu'} size={20} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="pl-mobile-nav" id="public-mobile-nav">
          <nav className="pl-mobile-nav__inner" aria-label="Mobile navigation">
            <Link to="/templates" onClick={closeMenu}>Templates</Link>
            <Link to="/pricing" onClick={closeMenu}>Pricing</Link>
            <Link to="/help/getting-started" onClick={closeMenu}>Guide</Link>
            {user ? (
              <Link to="/dashboard/sites" onClick={closeMenu}>Dashboard</Link>
            ) : (
              <Link to="/login" onClick={closeMenu}>Sign in</Link>
            )}
            <Link
              className="sarnia-btn sarnia-btn--primary"
              to={user ? '/dashboard/sites' : '/signup'}
              onClick={closeMenu}
            >
              {user ? 'Dashboard' : 'Start building'}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export function PublicFooter() {
  return (
    <footer className="pl-footer">
      <div className="sarnia-container pl-footer__inner">
        <div className="pl-footer__brand">
          <Link className="pl-logo pl-logo--footer" to="/">
            <span className="pl-logo__mark" aria-hidden="true">
              <Icon name="zap" size={16} />
            </span>
            <span className="pl-logo__wordmark">Sarnia Builder</span>
          </Link>
          <p className="pl-footer__tagline">Small island. Big signal.</p>
          <p className="pl-footer__made">Made in Guernsey</p>
        </div>
        <div className="pl-footer__columns">
          <div className="pl-footer__col">
            <h2 className="pl-footer__heading">Product</h2>
            <Link to="/templates">Templates</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/signup">Start building</Link>
          </div>
          <div className="pl-footer__col">
            <h2 className="pl-footer__heading">Resources</h2>
            <Link to="/help/getting-started">Guide</Link>
            <Link to="/">Home</Link>
          </div>
          <div className="pl-footer__col">
            <h2 className="pl-footer__heading">Legal</h2>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/abuse">Report abuse</Link>
          </div>
        </div>
      </div>
      <div className="pl-footer__bottom">
        <div className="sarnia-container">
          © {new Date().getFullYear()} Sarnia Digital. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <div className="pl-shell">
      <SiteSwitcher />
      <PublicHeader />
      <main className="pl-main">{children}</main>
      <PublicFooter />
    </div>
  );
}

export { PublicLayout };
export default PublicLayout;
